import React from 'react';
import PropTypes from 'prop-types';

import {
  Avatar,
  Button,
  Heading,
  Icon,
  Input,
  InputTyping,
  Modal,
} from '@drivesally/sally-ui';

import getTeamLabel from '../../lib/getTeamLabel.lib.crm';

import ActionButton from './components/ActionButton';
import Container from './components/Container';
import EmptyState from './components/EmptyState';
import Loader from './components/Loader';
import MemberRow from './components/MemberRow';
import MembersBody from './components/MembersBody';
import MembersContainer from './components/MembersContainer';
import MembersHeader from './components/MembersHeader';
import NewTeamRow from './components/NewTeamRow';
import Scroller from './components/Scroller';
import SearchContainer from './components/SearchContainer';
import Section from './components/Section';
import SectionHeader from './components/SectionHeader';
import SlackChannelError from './components/SlackChannelError';
import SlackChannelRow from './components/SlackChannelRow';
import SlackChannelSection from './components/SlackChannelSection';
import TeamRow from './components/TeamRow';
import TeamsHeader from './components/TeamsHeader';
import TeamsList from './components/TeamsList';
import UserName from './components/UserName';
import UserNameRow from './components/UserNameRow';

// Team creation is disabled for now -- teams are created directly in the DB
// so `name` (Chaya's routing key) stays under manual control. Flip this back
// on if/when self-service creation is reintroduced.
const CREATE_TEAM_ENABLED = false;

const getUserName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '-';

const renderUserRow = ({action, disabled, onAction, user}) => (
  <MemberRow key={user.id}>
    <ActionButton
      type="button"
      action={action}
      disabled={disabled}
      onClick={onAction(user.id)}
    >
      <Icon icon={action === 'add' ? 'plus' : 'delete'} />
    </ActionButton>
    <Avatar avatar={user.username} size="large" />
    <UserNameRow>
      {user.username || '-'}
      <UserName>{getUserName(user)}</UserName>
    </UserNameRow>
  </MemberRow>
);

const CrmTeamsModal = ({
  assignedUsers,
  availableUsers,
  creating,
  loading,
  newTeam,
  onClose,
  onCreateTeam,
  onMemberAdd,
  onMemberRemove,
  onNewTeamChange,
  onSearch,
  onSlackChannelApply,
  onSlackChannelChange,
  onTeamSelect,
  saving,
  search,
  selectedTeam,
  slackChannelDirty,
  slackChannelError,
  slackChannelName,
  slackChannelSaving,
  teams,
  visible,
}) => (
  <Modal
    title="CRM Teams"
    icon="team"
    size="large"
    visible={visible}
    onClose={onClose}
  >
    {(Content) => (
      <Content padding="none">
        {loading ? (
          <Container>
            <Loader>
              <Icon icon="loading2" spin />
            </Loader>
          </Container>
        ) : (
          <Container>
            <TeamsList fullWidth={!selectedTeam}>
              <TeamsHeader>
                <Heading size="4">Teams</Heading>
              </TeamsHeader>
              {CREATE_TEAM_ENABLED && (
                <NewTeamRow>
                  <InputTyping
                    value={newTeam}
                    onChange={onNewTeamChange}
                    placeholder="New team name"
                    disabled={creating}
                  />
                  <Button
                    size="small"
                    loading={creating}
                    disabled={!newTeam.trim().length || creating}
                    onClick={onCreateTeam}
                  >
                    Add
                  </Button>
                </NewTeamRow>
              )}
              <Scroller>
                {teams.map((team) => (
                  <TeamRow
                    key={team.id}
                    selected={selectedTeam?.id === team.id}
                    onClick={onTeamSelect(team)}
                  >
                    {getTeamLabel(team)}
                  </TeamRow>
                ))}
              </Scroller>
            </TeamsList>
            {!!selectedTeam && (
              <MembersContainer>
                <MembersHeader>
                  <Heading size="4">
                    {getTeamLabel(selectedTeam)} Members
                  </Heading>
                </MembersHeader>
                <SlackChannelSection>
                  <SectionHeader>Slack Channel</SectionHeader>
                  <SlackChannelRow>
                    <Input
                      value={slackChannelName}
                      onChange={onSlackChannelChange}
                      placeholder="e.g. nyc_crm"
                      disabled={slackChannelSaving}
                      error={!!slackChannelError}
                    />
                    <Button
                      size="small"
                      loading={slackChannelSaving}
                      disabled={!slackChannelDirty || slackChannelSaving}
                      onClick={onSlackChannelApply}
                    >
                      Apply
                    </Button>
                  </SlackChannelRow>
                  {!!slackChannelError && (
                    <SlackChannelError>{slackChannelError}</SlackChannelError>
                  )}
                </SlackChannelSection>
                <MembersBody>
                  <Section>
                    <SectionHeader>
                      Assigned Members ({assignedUsers.length})
                    </SectionHeader>
                    <Scroller>
                      {!assignedUsers.length ? (
                        <EmptyState>No members assigned to this team.</EmptyState>
                      ) : (
                        assignedUsers.map((user) =>
                          renderUserRow({
                            action: 'remove',
                            disabled: saving,
                            onAction: onMemberRemove,
                            user,
                          })
                        )
                      )}
                    </Scroller>
                  </Section>
                  <Section>
                    <SectionHeader>Add Members</SectionHeader>
                    <SearchContainer>
                      <InputTyping
                        value={search}
                        onChange={onSearch}
                        placeholder="Search users to add"
                        preIcon="search1"
                      />
                    </SearchContainer>
                    <Scroller>
                      {!search.trim().length ? (
                        <EmptyState>
                          Search by name, username, or email to add members.
                        </EmptyState>
                      ) : !availableUsers.length ? (
                        <EmptyState>No matching users found.</EmptyState>
                      ) : (
                        availableUsers.map((user) =>
                          renderUserRow({
                            action: 'add',
                            disabled: saving,
                            onAction: onMemberAdd,
                            user,
                          })
                        )
                      )}
                    </Scroller>
                  </Section>
                </MembersBody>
              </MembersContainer>
            )}
          </Container>
        )}
      </Content>
    )}
  </Modal>
);

CrmTeamsModal.propTypes = {
  assignedUsers: PropTypes.array,
  availableUsers: PropTypes.array,
  creating: PropTypes.bool,
  loading: PropTypes.bool,
  newTeam: PropTypes.string,
  onClose: PropTypes.func,
  onCreateTeam: PropTypes.func,
  onMemberAdd: PropTypes.func,
  onMemberRemove: PropTypes.func,
  onNewTeamChange: PropTypes.func,
  onSearch: PropTypes.func,
  onSlackChannelApply: PropTypes.func,
  onSlackChannelChange: PropTypes.func,
  onTeamSelect: PropTypes.func,
  saving: PropTypes.bool,
  search: PropTypes.string,
  selectedTeam: PropTypes.object,
  slackChannelDirty: PropTypes.bool,
  slackChannelError: PropTypes.string,
  slackChannelName: PropTypes.string,
  slackChannelSaving: PropTypes.bool,
  teams: PropTypes.array,
  visible: PropTypes.bool,
};

export default CrmTeamsModal;
